import { Tooltip } from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import { Trigger } from '@radix-ui/react-context-menu';
import { useParams } from '@tanstack/react-router';
import { generateKeyBetween } from 'fractional-indexing';
import { nanoid } from 'nanoid';
import type React from 'react';
import { useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Content from '~/components2/ContextMenu/Content';
import ContextMenu from '~/components2/ContextMenu/ContextMenu';
import Item from '~/components2/ContextMenu/Item';
import Separator from '~/components2/ContextMenu/Separator';
import SubMenu from '~/components2/ContextMenu/SubMenu';
import type { MapStoreBase } from '~/components2/Map';
import { Notify } from '~/components2/Notify';
import { useReportProblemEnabled } from '~/hooks/useReportProblemEnabled';
import rootStore from '~/store';

// Dropdown-matching styles
const contextContentClassName = cn(
  'bg-white text-primary border border-grey-30 rounded-sm !p-1 shadow-md',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
  'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  'z-50 min-w-32 overflow-x-hidden overflow-y-auto',
);
const contextItemClassName = cn(
  '!relative flex cursor-default items-center gap-2 rounded-sm !px-2 !py-1.5 text-xs outline-none select-none',
  'text-primary-dark hover:!bg-grey-10 focus:bg-grey-10 data-[highlighted]:!bg-grey-10',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
);
const contextSubTriggerClassName = cn(
  'flex cursor-default items-center gap-2 rounded-sm !px-2 !py-1.5 text-xs outline-none select-none',
  'text-primary-dark hover:!bg-grey-10 focus:bg-grey-10 data-[highlighted]:!bg-grey-10 data-[state=open]:!bg-grey-10',
  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
);
const contextSeparatorClassName = 'bg-grey-30 -mx-1 my-1 h-px';

interface MapContextMenuProps {
  store: MapStoreBase;
  allowObjectOrdering?: boolean;
  allowComments?: boolean;
  streetViewMode?: 'external' | 'internal';
  projectId?: string;
  currentMapId?: string;
  children: React.ReactNode;
}

export default function MapContextMenu({
  store,
  allowComments = false,
  allowObjectOrdering = false,
  streetViewMode = 'external',
  projectId,
  currentMapId,
  children,
}: MapContextMenuProps) {
  const map = useMap();
  const { objectsOnMainSurface, currentObject } = useSnapshot(store) as typeof store;
  const [pointRef, setPointRef] = useState({ lng: 0, lat: 0 });
  const layerAttributesChosenRef = useRef(false);
  const reportProblemEnabled = useReportProblemEnabled();
  const { tabId } = useParams({ strict: false });

  function getMaxIndex() {
    if (currentObject) {
      const objects = objectsOnMainSurface;
      const currentIndex = currentObject.sortOrder;

      let maxIndex = currentIndex;

      for (const key in objects) {
        const comparisonIndex = objects[key].sortOrder;
        if (comparisonIndex.localeCompare(maxIndex) === 1) {
          maxIndex = comparisonIndex;
        }
      }

      return maxIndex;
    }
  }

  function getMinIndex() {
    if (currentObject) {
      const objects = objectsOnMainSurface;
      const currentIndex = currentObject.sortOrder;

      let minIndex = currentIndex;

      for (const key in objects) {
        const comparisonIndex = objects[key].sortOrder;
        if (comparisonIndex.localeCompare(minIndex) === -1) {
          minIndex = comparisonIndex;
        }
      }

      return minIndex;
    }
  }

  function bringToFront() {
    if (currentObject) {
      const maxIndex = getMaxIndex();
      const newSortOrder = generateKeyBetween(maxIndex, null);
      store.updateObject({ sortOrder: newSortOrder });
    } else {
      return;
    }
  }

  function sendToBack() {
    if (currentObject) {
      const minIndex = getMinIndex();
      const newSortOrder = generateKeyBetween('a0', minIndex);
      store.updateObject({ sortOrder: newSortOrder });
    } else {
      return;
    }
  }

  function bringForward() {
    if (currentObject) {
      const objects = objectsOnMainSurface;
      const currentIndex = currentObject.sortOrder;

      let lesserIndex = currentIndex;

      for (const key in objects) {
        const comparisonIndex = objects[key].sortOrder;
        if (comparisonIndex.localeCompare(currentIndex) === 1) {
          if (lesserIndex === currentIndex) {
            lesserIndex = comparisonIndex;
          } else if (comparisonIndex.localeCompare(lesserIndex) === -1) {
            lesserIndex = comparisonIndex;
          }
        }
      }

      let greaterIndex = lesserIndex;

      for (const key in objects) {
        const comparisonIndex = objects[key].sortOrder;
        if (comparisonIndex.localeCompare(lesserIndex) === 1) {
          if (greaterIndex === lesserIndex) {
            greaterIndex = comparisonIndex;
          } else if (comparisonIndex.localeCompare(greaterIndex) === -1) {
            greaterIndex = comparisonIndex;
          }
        }
      }

      let newSortOrder: string;
      if (greaterIndex === lesserIndex) {
        newSortOrder = generateKeyBetween(lesserIndex, null);
      } else {
        newSortOrder = generateKeyBetween(lesserIndex, greaterIndex);
      }

      store.updateObject({ sortOrder: newSortOrder });
    } else {
      return;
    }
  }

  function sendBackward() {
    if (currentObject) {
      const objects = objectsOnMainSurface;
      const currentIndex = currentObject.sortOrder;

      let greaterIndex = currentIndex;

      for (const key in objects) {
        const comparisonIndex = objects[key].sortOrder;
        if (comparisonIndex.localeCompare(currentIndex) === -1) {
          if (greaterIndex === currentIndex) {
            greaterIndex = comparisonIndex;
          } else if (comparisonIndex.localeCompare(greaterIndex) === 1) {
            greaterIndex = comparisonIndex;
          }
        }
      }

      let lesserIndex = greaterIndex;

      for (const key in objects) {
        const comparisonIndex = objects[key].sortOrder;
        if (comparisonIndex.localeCompare(greaterIndex) === -1) {
          if (lesserIndex === greaterIndex) {
            lesserIndex = comparisonIndex;
          } else if (comparisonIndex.localeCompare(lesserIndex) === 1) {
            lesserIndex = comparisonIndex;
          }
        }
      }

      let newSortOrder: string;
      if (lesserIndex === greaterIndex) {
        newSortOrder = generateKeyBetween('a0', lesserIndex);
      } else {
        newSortOrder = generateKeyBetween(lesserIndex, greaterIndex);
      }

      store.updateObject({ sortOrder: newSortOrder });
    } else {
      return;
    }
  }

  function openInGoogleMaps() {
    // NOTE: adding 1.5 to zoom level to fix the different scaling between Mapbox and Google Maps
    const url = `https://www.google.com/maps/@?api=1&map_action=map&center=${
      pointRef.lat
    },${pointRef.lng}&zoom=${Math.round(map.current!.getZoom() + 1.5)}`;
    window.open(url, '_blank');

    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawOpenedInGoogleMaps, {
      organization_id: rootStore.currentOrganizationId || undefined,
      ...(projectId && { project_id: projectId }),
      ...(currentMapId && { map_id: currentMapId }),
    });
    return;
  }

  function copyCoordinates() {
    const latLngText = `${pointRef.lat}, ${pointRef.lng}`;
    navigator.clipboard.writeText(latLngText);
    captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawCoordinatesCopied, {
      organization_id: rootStore.currentOrganizationId || undefined,
      ...(projectId && { project_id: projectId }),
      ...(currentMapId && { map_id: currentMapId }),
    });
    Notify.info({ title: '', message: 'Location copied to clipboard' });
    return;
  }

  function openStreetView() {
    if (streetViewMode === 'external') {
      const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${pointRef.lat},${pointRef.lng}`;
      window.open(url, '_blank');
      return;
    } else {
      store.setStreetViewState({
        visible: true,
        isNew: true,
        lng: pointRef.lng,
        lat: pointRef.lat,
        pitch: 0,
        heading: 0,
        zoom: 1,
      });
    }
  }

  const newComment = async () => {
    // immediately create a new comment object where the cursor brought up the context menu.
    await store.newObject({
      id: nanoid(),
      sortOrder: store.generateSortOrderKey(),
      type: 'comment',
      geometry: {
        type: 'Point',
        coordinates: [pointRef.lng, pointRef.lat],
      },
      title: '',
      description: '',
      properties: {},
    });
    // `store.newObject` already fires project_civildraw_object_added in PostHog,
    // so no additional capture is needed here.
  };

  const viewMetadata = async () => {
    layerAttributesChosenRef.current = true;
    store.setIdentifyDetailsPoint(pointRef);
  };

  const shareLocation = async () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/home?lng=${pointRef.lng}&lat=${pointRef.lat}&z=${map.current!.getZoom()}`,
    );
    Notify.info({ title: '', message: 'Share link copied to clipboard' });
  };

  const reportProblem = () => {
    if (!reportProblemEnabled) {
      return;
    }
    store.setReportProblemPoint(pointRef);
  };

  // If on a touch screen, don't render the context menu. This interferes with the touch gestures.
  if (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches) {
    return null;
  }

  return (
    <ContextMenu>
      <Trigger
        onContextMenu={(e) => {
          // Close the currently open metadata sidebar if open when opening the context menu.
          store.setIdentifyDetailsPoint(undefined);

          if (map) {
            const rect = map.current!.getCanvas().getBoundingClientRect();
            const { clientX, clientY } = e;
            const x = clientX - rect.x;
            const y = clientY - rect.y;
            const { lng, lat } = map.current!.unproject([x, y]);
            setPointRef({ lng, lat });
          }
        }}
      >
        {children}
      </Trigger>

      <Content
        className={contextContentClassName}
        data-testid="map-context-menu"
        onCloseAutoFocus={(event) => {
          // The Layer Attributes panel takes focus as it opens; returning focus to the map would take it back.
          const panelHasFocus = !!document.activeElement?.closest('[data-testid="metadata-sidebar"]');
          if (layerAttributesChosenRef.current && panelHasFocus) event.preventDefault();
          layerAttributesChosenRef.current = false;
        }}
      >
        <Item
          key="openMetadata"
          onClick={viewMetadata}
          className={contextItemClassName}
          data-testid="map-context-menu-view-metadata"
        >
          Layer Attributes
        </Item>
        <Separator className={contextSeparatorClassName} />
        <Item
          key="shareLocation"
          onClick={shareLocation}
          className={contextItemClassName}
          data-testid="map-context-menu-share-location"
        >
          Share Location
        </Item>
        <Separator className={contextSeparatorClassName} />
        {reportProblemEnabled && !!projectId && tabId === 'intelligence' && (
          <>
            <Item
              key="reportProblem"
              onClick={reportProblem}
              className={contextItemClassName}
              data-testid="map-context-menu-report-problem"
            >
              Request Data Review
            </Item>
            <Separator className={contextSeparatorClassName} />
          </>
        )}
        {allowComments && (
          <>
            <Can action={Action.CommentCivilDraw}>
              <Item
                key="newComment"
                onClick={newComment}
                className={contextItemClassName}
                data-testid="map-context-menu-add-comment"
              >
                Add comment
              </Item>
            </Can>
            <Separator className={contextSeparatorClassName} />
          </>
        )}

        <Item
          key="openStreetView"
          onClick={() => openStreetView()}
          className={contextItemClassName}
          data-testid="map-context-menu-street-view"
        >
          Open Street View
        </Item>
        <Item
          key="openGoogleMaps"
          onClick={() => openInGoogleMaps()}
          className={contextItemClassName}
          data-testid="map-context-menu-google-maps"
        >
          Open in Google Maps
        </Item>
        <Separator className={contextSeparatorClassName} />

        <Tooltip asChild text="Copy coordinates" side="bottom">
          <Item
            key="copyCoordinates"
            onClick={() => copyCoordinates()}
            className={contextItemClassName}
            data-testid="map-context-menu-copy-coordinates"
          >
            <LatLngDisplay lat={pointRef.lat} lng={pointRef.lng} />
          </Item>
        </Tooltip>

        {allowObjectOrdering && currentObject && (
          <>
            <Separator className={contextSeparatorClassName} />
            <SubMenu label="Arrange" className={contextSubTriggerClassName}>
              <Item onClick={() => bringToFront()} className={contextItemClassName}>
                Bring to Front
              </Item>
              <Item onClick={() => bringForward()} className={contextItemClassName}>
                Bring Forward
              </Item>
              <Item onClick={() => sendToBack()} className={contextItemClassName}>
                Send to Back
              </Item>
              <Item onClick={() => sendBackward()} className={contextItemClassName}>
                Send Backward
              </Item>
            </SubMenu>
          </>
        )}
      </Content>
    </ContextMenu>
  );
}

interface LatLngProps {
  lat: number;
  lng: number;
}

function LatLngDisplay({ lat, lng }: LatLngProps) {
  return (
    <>
      <span>{lat.toFixed(5)}</span>, <span>{lng.toFixed(5)}</span>
    </>
  );
}
